import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { sortCOPSModelToParam } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { resetNotification } from '../../common/navigation/containers/navigation-actions';
import {
  defaultWorklistId,
  planningBoardWorklistId,
  referenceTypes,
  ticketWorklistId,
  urlSigns,
  worklistDefaultSortParam,
} from '../../utils/app-configs';
import {
  addDataToNoneView,
  transformFiltersToUrlParams,
  transformUrlParamsToFilters,
} from '../../utils/helpers';
import {
  clearViews,
  getWorklistViews,
  setCurrentWorklistId,
  updateFilters,
  updateSort,
  updateView,
} from '../containers/worklist-actions';
import decideInitialView from '../worklist-helpers';

const WorklistWrapper = ({
  isTickets = false,
  isPlanningboard = false,
  match,
  children,
  history,
  location,
  intl,
  baseType = '',
}) => {
  const dispatch = useDispatch();
  const [prevViews, setPrevViews] = useState();

  const {
    activeFilters,
    activeSort,
    activeView,
    views,
    currentWorklistId,
    saveViewsSuccess,
  } = useSelector(state => state.worklist);
  const { defaultSorting } = useSelector(state => state.worklist.metaData);

  const activeWorklistId = match.params?.worklistId ||
    // eslint-disable-next-line no-undefined
    (location?.pathname?.includes('/tickets') ? ticketWorklistId : undefined);

  const getDefaultWorklistId = () => {
    if (isTickets) {
      return ticketWorklistId;
    }
    return isPlanningboard ? planningBoardWorklistId : defaultWorklistId;
  };

  const getBaseUrl = (worklistId) => {
    if (isTickets) {
      // eslint-disable-next-line max-len
      return `${match.params.objectId ? `${location.pathname.replace(/\/tickets.*/, '/tickets')}` : '/tickets'}${match.params.objectId ? `/object/${match.params.objectId}` : ''}`;
    }
    return isPlanningboard ? `/planning-board/${worklistId}` : `/worklists/${worklistId}`;
  };

  const getUrlExtras = () => {
    const { ganttFilters, ganttSort, ganttView } = match.params;
    if (isPlanningboard) {
      // eslint-disable-next-line max-len
      return `${ganttFilters ? `/ganttFilters/${ganttFilters}` : ''}${ganttSort ? `/ganttSort/${ganttSort}` : ''}${ganttView ? `/ganttView/${ganttView}` : ''}${location?.pathname?.endsWith('/filterselect') ? '/filterselect' : ''}${location?.pathname?.endsWith('/ganttfilterselect') ? '/ganttfilterselect' : ''}`;
    }
    return location?.pathname?.endsWith('filterselect') ? '/filterselect' : '';
  };

  const clearAll = () => {
    dispatch(setCurrentWorklistId());
    dispatch(clearViews());
    dispatch(updateView(null));
    dispatch(updateSort(''));
    dispatch(updateFilters({}));
  };

  const fetchWorklistData = ({ id, urlString, filters, sort, view }) => {
    const defaultId = getDefaultWorklistId();
    const urlParams = urlString || transformFiltersToUrlParams(filters || {});
    const worklistId = id || match.params.worklistId || defaultId;
    const sortParam = sort;
    const viewParam = view || match.params.view;
    const baseUrl = getBaseUrl(worklistId);
    const urlExtras = getUrlExtras();
    history.replace(
      // eslint-disable-next-line
      `${baseUrl}${urlParams ? `/filters/${urlParams}` : ''}${sortParam ? `/sort/${sortParam}` : ''
      }${viewParam ? `/view/${viewParam}` : ''}${urlExtras}`,
    );
  };

  useEffect(() => {
    if (isTickets) {
      dispatch(getWorklistViews({ id: ticketWorklistId }));
    }
    return () => clearAll();
  }, []);

  useEffect(() => {
    const { worklistId } = match.params;
    clearAll();
    dispatch(setCurrentWorklistId(isTickets ? ticketWorklistId : match.params?.worklistId));
    if (worklistId && !isTickets) {
      dispatch(getWorklistViews({ id: worklistId }));
    }
  }, [match.params.worklistId]);

  useDeepCompareEffect(() => {
    // If the view changed, update the filters and sort to the ones of the view
    if (activeView?.id && currentWorklistId === activeWorklistId) {
      fetchWorklistData({
        id: isTickets ? ticketWorklistId : null,
        urlString: transformFiltersToUrlParams(activeFilters || {}),
        filters: activeFilters,
        sort: activeSort || defaultSorting,
        view: activeView?.id,
      });
    }
  }, [activeSort, activeFilters]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (activeView?.id && currentWorklistId === activeWorklistId) {
      // Update the url but only when the view changes (sorting & filters didn't)
      if (activeSort === sortCOPSModelToParam(activeView?.sorting) &&
        transformFiltersToUrlParams(activeFilters) === activeView?.filterData
      ) {
        fetchWorklistData({
          id: isTickets ? ticketWorklistId : null,
          urlString: transformFiltersToUrlParams(activeFilters || {}),
          filters: activeFilters,
          sort: activeSort || defaultSorting,
          view: activeView?.id,
        });
      }

      // Update sorting/filters
      if (currentWorklistId === activeWorklistId) {
        dispatch(updateSort(sortCOPSModelToParam(activeView.sorting)));
        const viewFilters = transformUrlParamsToFilters(activeView.filterData || '');
        const objectFilters = viewFilters?.object || [];

        // If we are on the ticketsubpage of an object,
        // we want to default the object filter to the current object and make it disabled
        // (we don't want to lose options that are already selected so we spread them).
        // unless we already have an object filter in with the same id. then we use the viewFilters
        const filters = (isTickets && (match.params.objectId && !objectFilters.length)) ||
          (objectFilters[0]?.startsWith('showChildren') ?
            match?.params?.objectId && !objectFilters[0]?.includes(match.params.objectId) :
            match?.params?.objectId && !objectFilters?.includes(match.params.objectId)
          ) ? {
          ...viewFilters,
          object:
            // eslint-disable-next-line no-nested-ternary
            baseType === referenceTypes.installedAt ?
              // eslint-disable-next-line max-len
              [`showChildren[${match.params.objectId}${objectFilters.length ? urlSigns.valueSeparator : ''}${objectFilters.join(urlSigns.valueSeparator)}]`] :
              objectFilters[0]?.startsWith('showChildren') ?
                // eslint-disable-next-line max-len
                [`showChildren[${match.params.objectId}${objectFilters.length ? urlSigns.valueSeparator : ''}${objectFilters[0].replace('showChildren[', '').replace(']', '')}]`] :
                [match.params.objectId, ...objectFilters],
        } : viewFilters;

        dispatch(updateFilters(filters));
      }
    }
  }, [activeView?.id]);

  // Don't use deepcompare here since views can be null to avoid a rerender
  // Track issue: https://github.com/kentcdodds/use-deep-compare-effect/issues/63
  useEffect(() => {
    if (
      views &&
      currentWorklistId === activeWorklistId
    ) {
      const viewsArray = addDataToNoneView(
        activeWorklistId, views, null, defaultSorting,
      );
      const initialView = decideInitialView(
        viewsArray,
        match,
        isTickets,
        defaultSorting || worklistDefaultSortParam,
      );
      dispatch(updateView(initialView));
      setPrevViews(viewsArray);
    }
  }, [JSON.stringify(views)]);

  useEffect(() => {
    if (saveViewsSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'worklist.filters.SAVE_VIEW_SUCCESS' }),
        placement: 'top',
      });
      dispatch(resetNotification());

      const worklistId = isTickets ? ticketWorklistId : match.params.worklistId;
      const viewsArray = addDataToNoneView(worklistId, views, null, defaultSorting);

      // If a view is added go to the new view
      if (viewsArray?.length === prevViews?.length + 1) {
        const addedViews = viewsArray?.filter(view =>
          !prevViews?.find(ev => ev.id === view.id),
        );
        if (addedViews?.length) {
          dispatch(
            updateView(addedViews[0]),
          );
          return;
        }
      }

      // Default view has changed
      if (
        viewsArray?.find(v => v.isDefault)?.id !== prevViews?.find(v => v.isDefault)?.id
      ) {
        dispatch(
          updateView(viewsArray?.find(v => v.isDefault) || viewsArray?.find(v => v.isNone)),
        );
        return;
      }

      // If length is the same.
      // Either the order has changed or a singe view was update.
      // Do nothing in this case
      if (
        viewsArray?.length === prevViews?.id
      ) {
        return;
      }

      // If the activeview is deleted go to default or none
      // else stay on the activeview
      if (!viewsArray?.find(v => v.id === activeView?.id)) {
        dispatch(
          updateView(viewsArray?.find(v => v.isDefault) || viewsArray?.find(v => v.isNone)),
        );
      }
    }
  }, [saveViewsSuccess]);

  return <>{children}</>;
};

WorklistWrapper.propTypes = {
  isTickets: PropTypes.bool,
  isPlanningboard: PropTypes.bool,
  match: PropTypes.object,
  children: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  intl: PropTypes.object,
  baseType: PropTypes.string,
};

export default injectIntl(withRouter(WorklistWrapper));
